// In-App Template Editor Component per ORIGINAL_REQUEST § R2 & PROJECT.md F2.2
import React, { useState, useEffect } from 'react';
import type {
  ChecklistTemplate,
  ChecklistItem,
  FrequencyType,
  InputType,
} from '../../types';
import { getAllTemplates, saveTemplate, deleteTemplate } from '../../db';
import { validateChecklistTemplate } from '../../services/excel';
import { Button, Card, Modal } from '../common';
import {
  FileText,
  PlusCircle,
  Pencil,
  Trash2,
  Save,
  CheckCircle2,
  AlertCircle,
  Gauge,
  CheckSquare,
  ListFilter,
  Layers,
  ChevronRight,
  Clock,
} from 'lucide-react';
import {
  broadcastSystemData,
  subscribeSystemDataUpdates,
} from '../../services/config/cloudConfigService';

export const TemplateEditor: React.FC = () => {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [activeFrequency, setActiveFrequency] = useState<FrequencyType>('DAILY');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Working copy of current template
  const [currentTemplate, setCurrentTemplate] = useState<ChecklistTemplate | null>(null);

  // Edit/Add Item Modal
  const [isItemModalOpen, setIsItemModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [deleteItemConfirmId, setDeleteItemConfirmId] = useState<string | null>(null);

  // Item Form Fields
  const [itemFormNo, setItemFormNo] = useState<number>(1);
  const [itemFormTaskTh, setItemFormTaskTh] = useState<string>('');
  const [itemFormTaskEn, setItemFormTaskEn] = useState<string>('');
  const [itemFormCriteriaTh, setItemFormCriteriaTh] = useState<string>('');
  const [itemFormCriteriaEn, setItemFormCriteriaEn] = useState<string>('');
  const [itemFormMethodTh, setItemFormMethodTh] = useState<string>('');
  const [itemFormMethodEn, setItemFormMethodEn] = useState<string>('');
  const [itemFormInputType, setItemFormInputType] = useState<InputType>('select_option');
  const [itemFormUnit, setItemFormUnit] = useState<string>('MPa');
  const [itemFormMinValue, setItemFormMinValue] = useState<string>('');
  const [itemFormMaxValue, setItemFormMaxValue] = useState<string>('');
  const [itemFormWarningMsg, setItemFormWarningMsg] = useState<string>('');
  const [itemFormOptions, setItemFormOptions] = useState<string[]>(['ปกติ', 'ชำรุด']);
  const [itemFormHoursThreshold, setItemFormHoursThreshold] = useState<string>('500');
  const [itemModalError, setItemModalError] = useState<string>('');

  const loadTemplates = async (selectId?: string) => {
    setIsLoading(true);
    try {
      const list = await getAllTemplates();
      setTemplates(list);
      const targetId = selectId || selectedTemplateId || list[0]?.id;
      if (targetId) {
        const found = list.find((t) => t.id === targetId) || list[0];
        if (found) {
          setSelectedTemplateId(found.id);
          // Clone deeply to allow in-memory editing
          setCurrentTemplate(JSON.parse(JSON.stringify(found)));
        }
      }
    } catch (err) {
      console.error('[TemplateEditor] Error loading templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    const unsubscribe = subscribeSystemDataUpdates(() => {
      loadTemplates();
    });
    return unsubscribe;
  }, []);

  const handleSelectTemplate = (id: string) => {
    setSelectedTemplateId(id);
    const found = templates.find((t) => t.id === id);
    if (found) {
      setCurrentTemplate(JSON.parse(JSON.stringify(found)));
      setSaveSuccessMsg('');
      setErrorMsg('');
    }
  };

  const openAddItemModal = () => {
    if (!currentTemplate) return;
    const currentFreqItems = currentTemplate.items.filter(
      (i) => i.frequency === activeFrequency
    );
    const nextNo =
      currentFreqItems.length > 0
        ? Math.max(...currentFreqItems.map((i) => i.itemNo)) + 1
        : 1;

    setEditingItem(null);
    setItemFormNo(nextNo);
    setItemFormTaskTh('');
    setItemFormTaskEn('');
    setItemFormCriteriaTh('');
    setItemFormCriteriaEn('');
    setItemFormMethodTh('สายตา (Visual)');
    setItemFormMethodEn('');
    setItemFormInputType('select_option');
    setItemFormUnit('MPa');
    setItemFormMinValue('');
    setItemFormMaxValue('');
    setItemFormWarningMsg('');
    setItemFormOptions(['ปกติ', 'ชำรุด']);
    setItemFormHoursThreshold('500');
    setItemModalError('');
    setIsItemModalOpen(true);
  };

  const openEditItemModal = (item: ChecklistItem) => {
    setEditingItem(item);
    setItemFormNo(item.itemNo);
    setItemFormTaskTh(item.taskNameTh);
    setItemFormTaskEn(item.taskNameEn || '');
    setItemFormCriteriaTh(item.criteriaTh);
    setItemFormCriteriaEn(item.criteriaEn || '');
    setItemFormMethodTh(item.methodTh);
    setItemFormMethodEn(item.methodEn || '');
    setItemFormInputType(item.inputType);
    setItemFormUnit(item.unit || 'MPa');
    setItemFormMinValue(item.minValue !== undefined ? String(item.minValue) : '');
    setItemFormMaxValue(item.maxValue !== undefined ? String(item.maxValue) : '');
    setItemFormWarningMsg(item.criticalLimit?.warningMessage || '');
    setItemFormOptions(
      item.options && item.options.length >= 2 ? [...item.options] : ['ปกติ', 'ชำรุด']
    );
    setItemFormHoursThreshold(
      item.hoursThreshold ? String(item.hoursThreshold) : '500'
    );
    setItemModalError('');
    setIsItemModalOpen(true);
  };

  const handleSaveItemModal = (e: React.FormEvent) => {
    e.preventDefault();
    setItemModalError('');

    if (!itemFormTaskTh.trim()) {
      setItemModalError('กรุณาระบุชื่องานตรวจเช็ก (Task Name)');
      return;
    }
    if (!itemFormCriteriaTh.trim()) {
      setItemModalError('กรุณาระบุเกณฑ์การตัดสิน (Criteria)');
      return;
    }
    if (!itemFormMethodTh.trim()) {
      setItemModalError('กรุณาระบุวิธีตรวจเช็ก (Checking Method)');
      return;
    }

    if (itemFormInputType === 'numeric_gauge' && !itemFormUnit.trim()) {
      setItemModalError('สำหรับเกจวัด จำเป็นต้องระบุหน่วยวัด (Unit เช่น MPa)');
      return;
    }

    if (itemFormInputType === 'select_option') {
      const validOptions = itemFormOptions.map((o) => o.trim()).filter(Boolean);
      if (validOptions.length < 2) {
        setItemModalError('สำหรับตัวเลือก จำเป็นต้องมีอย่างน้อย 2 ตัวเลือกขึ้นไป');
        return;
      }
    }

    if (activeFrequency === 'HOURS') {
      const hoursNum = parseInt(itemFormHoursThreshold, 10);
      if (isNaN(hoursNum) || hoursNum <= 0) {
        setItemModalError('สำหรับความถี่รอบชั่วโมง จำเป็นต้องระบุเกณฑ์ชั่วโมงที่มากกว่า 0');
        return;
      }
    }

    if (!currentTemplate) return;

    const itemId = editingItem
      ? editingItem.id
      : `${activeFrequency.toLowerCase()}_${itemFormNo}_${Date.now().toString().slice(-4)}`;

    const newItem: ChecklistItem = {
      id: itemId,
      itemNo: itemFormNo,
      frequency: activeFrequency,
      taskNameTh: itemFormTaskTh.trim(),
      criteriaTh: itemFormCriteriaTh.trim(),
      methodTh: itemFormMethodTh.trim(),
      inputType: itemFormInputType,
    };

    if (itemFormTaskEn.trim()) newItem.taskNameEn = itemFormTaskEn.trim();
    if (itemFormCriteriaEn.trim()) newItem.criteriaEn = itemFormCriteriaEn.trim();
    if (itemFormMethodEn.trim()) newItem.methodEn = itemFormMethodEn.trim();

    if (itemFormInputType === 'numeric_gauge') {
      newItem.unit = itemFormUnit.trim();
      if (itemFormMinValue.trim() !== '') {
        newItem.minValue = parseFloat(itemFormMinValue);
        newItem.criticalLimit = {
          operator: '<',
          value: parseFloat(itemFormMinValue),
          warningMessage:
            itemFormWarningMsg.trim() ||
            `ค่าที่วัดได้ต่ำกว่าเกณฑ์มาตรฐาน ${itemFormMinValue} ${itemFormUnit}`,
        };
      }
      if (itemFormMaxValue.trim() !== '') {
        newItem.maxValue = parseFloat(itemFormMaxValue);
      }
    } else if (itemFormInputType === 'select_option') {
      newItem.options = itemFormOptions.map((o) => o.trim()).filter(Boolean);
    }

    if (activeFrequency === 'HOURS') {
      newItem.hoursThreshold = parseInt(itemFormHoursThreshold, 10);
    }

    // Update in-memory template
    const updatedItems = [...currentTemplate.items];
    if (editingItem) {
      const idx = updatedItems.findIndex((i) => i.id === editingItem.id);
      if (idx !== -1) updatedItems[idx] = newItem;
    } else {
      updatedItems.push(newItem);
    }

    setCurrentTemplate({
      ...currentTemplate,
      items: updatedItems,
    });

    setIsItemModalOpen(false);
  };

  const handleDeleteItem = (id: string) => {
    if (!currentTemplate) return;
    if (currentTemplate.items.length <= 1) {
      setErrorMsg('ไม่สามารถลบได้: เทมเพลตต้องมีรายการตรวจสอบอย่างน้อย 1 รายการ');
      return;
    }
    const updated = currentTemplate.items.filter((i) => i.id !== id);
    setCurrentTemplate({
      ...currentTemplate,
      items: updated,
    });
    setDeleteItemConfirmId(null);
  };

  const handleSaveEntireTemplate = async () => {
    if (!currentTemplate) return;
    setErrorMsg('');
    setSaveSuccessMsg('');

    const validation = validateChecklistTemplate(currentTemplate);
    if (!validation.valid) {
      setErrorMsg(`ข้อมูลไม่ถูกต้อง: ${validation.errors.join(', ')}`);
      return;
    }

    try {
      await saveTemplate(currentTemplate);
      setSaveSuccessMsg('บันทึกการเปลี่ยนแปลงเทมเพลตและกระจายข้อมูลไปยังทุกเครื่องเรียบร้อย');
      await loadTemplates(currentTemplate.id);

      // Broadcast centralized template changes to Server & Google Sheets
      broadcastSystemData({
        reason: `อัปเดตแม่แบบ ${currentTemplate.name}`,
      }).catch(() => {});

      setTimeout(() => setSaveSuccessMsg(''), 4500);
    } catch (err: any) {
      setErrorMsg(`บันทึกไม่สำเร็จ: ${err.message || 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return <div className="p-12 text-center text-slate-400">กำลังโหลดเทมเพลต...</div>;
  }

  const currentFrequencyItems =
    currentTemplate?.items.filter((i) => i.frequency === activeFrequency) || [];

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Top Header & Template Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            เครื่องมือแก้ไขเทมเพลต (In-App Template Editor)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            ปรับแต่งรายการตรวจเช็ก เกณฑ์ตัดสิน วิธีการตรวจ ขีดจำกัด และรอบความถี่
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={selectedTemplateId}
            onChange={(e) => handleSelectTemplate(e.target.value)}
            className="bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-900 min-h-[48px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.items.length} รายการ)
              </option>
            ))}
          </select>

          <Button
            variant="success"
            onClick={handleSaveEntireTemplate}
            leftIcon={<Save className="w-5 h-5" />}
          >
            บันทึกเทมเพลต
          </Button>
        </div>
      </div>

      {/* Notifications */}
      {saveSuccessMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm rounded-xl flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
          <span>{saveSuccessMsg}</span>
        </div>
      )}
      {errorMsg && (
        <div className="p-4 bg-rose-50 border border-rose-300 text-rose-800 text-sm rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5 shrink-0 text-rose-600" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Template Metadata Editor Card */}
      {currentTemplate && (
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ชื่อเทมเพลต (Template Name)
              </label>
              <input
                type="text"
                value={currentTemplate.name}
                onChange={(e) =>
                  setCurrentTemplate({ ...currentTemplate, name: e.target.value })
                }
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                รุ่นเครื่องจักร (Target Machine Model)
              </label>
              <input
                type="text"
                value={currentTemplate.machineModel}
                onChange={(e) =>
                  setCurrentTemplate({
                    ...currentTemplate,
                    machineModel: e.target.value,
                  })
                }
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                เวอร์ชัน (Version)
              </label>
              <input
                type="text"
                value={currentTemplate.version}
                onChange={(e) =>
                  setCurrentTemplate({ ...currentTemplate, version: e.target.value })
                }
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none font-mono"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Frequency Tabs Navigation */}
      <div className="flex border-b border-slate-200 gap-1 overflow-x-auto pb-1">
        {[
          { key: 'DAILY', label: 'ประจำวัน (Daily)', count: currentTemplate?.items.filter((i) => i.frequency === 'DAILY').length || 0 },
          { key: 'WEEKLY', label: 'ประจำสัปดาห์ (Weekly)', count: currentTemplate?.items.filter((i) => i.frequency === 'WEEKLY').length || 0 },
          { key: 'MONTHLY', label: 'ประจำเดือน (Monthly)', count: currentTemplate?.items.filter((i) => i.frequency === 'MONTHLY').length || 0 },
          { key: 'HOURS', label: 'รอบชั่วโมง (Operating Hours)', count: currentTemplate?.items.filter((i) => i.frequency === 'HOURS').length || 0 },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveFrequency(tab.key as FrequencyType)}
            className={`min-h-[48px] px-5 py-2.5 font-bold text-sm rounded-t-xl transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
              activeFrequency === tab.key
                ? 'bg-white text-emerald-700 border-emerald-600 shadow-xs'
                : 'text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-slate-100 text-slate-600">
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Action Bar for Items */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          แสดง {currentFrequencyItems.length} รายการสำหรับหมวดหมู่นี้
        </p>
        <Button
          size="sm"
          variant="primary"
          onClick={openAddItemModal}
          leftIcon={<PlusCircle className="w-4 h-4" />}
        >
          เพิ่มรายการในหมวดนี้
        </Button>
      </div>

      {/* Items List */}
      {currentFrequencyItems.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-xs">
          <Layers className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-800">ยังไม่มีรายการตรวจเช็กในหมวดนี้</p>
          <p className="text-xs text-slate-500 mt-1">คลิก "เพิ่มรายการในหมวดนี้" เพื่อสร้างรายการแรก</p>
        </div>
      ) : (
        <div className="space-y-3">
          {currentFrequencyItems.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all"
            >
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    #{item.itemNo}
                  </span>
                  <span className="text-sm font-bold text-slate-900">{item.taskNameTh}</span>

                  {item.hoursThreshold && (
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> ทุก {item.hoursThreshold.toLocaleString()} ชม.
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600 pt-1">
                  <div>
                    <span className="text-slate-500">เกณฑ์: </span>
                    <span className="text-slate-800 font-medium">{item.criteriaTh}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">วิธี: </span>
                    <span className="text-slate-800">{item.methodTh}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1 flex-wrap text-xs">
                  <span className="text-slate-500">ประเภทข้อมูล:</span>
                  {item.inputType === 'numeric_gauge' && (
                    <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono flex items-center gap-1">
                      <Gauge className="w-3 h-3" /> เกจวัด ({item.unit})
                      {item.minValue !== undefined && ` Min: ${item.minValue}`}
                      {item.maxValue !== undefined && ` Max: ${item.maxValue}`}
                    </span>
                  )}
                  {item.inputType === 'done_checkbox' && (
                    <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                      <CheckSquare className="w-3 h-3" /> ทำแล้ว (Checkbox)
                    </span>
                  )}
                  {item.inputType === 'select_option' && (
                    <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                      <ListFilter className="w-3 h-3" /> ตัวเลือก: {item.options?.join(' / ')}
                    </span>
                  )}
                  {item.inputType === 'text' && (
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      ข้อความ (Text)
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEditItemModal(item)}
                  leftIcon={<Pencil className="w-4 h-4" />}
                >
                  แก้ไข
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteItemConfirmId(item.id)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  leftIcon={<Trash2 className="w-4 h-4" />}
                >
                  ลบ
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Add Item Modal */}
      <Modal
        isOpen={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        title={editingItem ? `แก้ไขรายการ #${itemFormNo}` : `เพิ่มรายการใหม่ในหมวด ${activeFrequency}`}
        maxWidth="lg"
      >
        <form onSubmit={handleSaveItemModal} className="space-y-4">
          {itemModalError && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{itemModalError}</span>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ลำดับ (Item No.) *
              </label>
              <input
                type="number"
                min="1"
                value={itemFormNo}
                onChange={(e) => setItemFormNo(parseInt(e.target.value, 10) || 1)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] font-mono focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                ประเภทช่องรับข้อมูล (Input Type) *
              </label>
              <select
                value={itemFormInputType}
                onChange={(e) => setItemFormInputType(e.target.value as InputType)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              >
                <option value="select_option">ตัวเลือก (Select / Radio เช่น ปกติ / ชำรุด)</option>
                <option value="numeric_gauge">เกจวัดตัวเลข (Numeric Gauge เช่น MPa, bar)</option>
                <option value="done_checkbox">เช็กบ็อกซ์ยืนยัน (Single Action Done Checkbox)</option>
                <option value="text">ข้อความอิสระ (Text)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              รายการบำรุงรักษา / ตรวจเช็ก (Task Name TH) *
            </label>
            <textarea
              rows={2}
              value={itemFormTaskTh}
              onChange={(e) => setItemFormTaskTh(e.target.value)}
              placeholder="เช่น ตรวจสอบแรงดันระบบไฮดรอลิกขณะทำงาน..."
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder-slate-400"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                เกณฑ์มาตรฐานความปลอดภัย (Criteria TH) *
              </label>
              <input
                type="text"
                value={itemFormCriteriaTh}
                onChange={(e) => setItemFormCriteriaTh(e.target.value)}
                placeholder="เช่น ต้องไม่ต่ำกว่า 6.8 MPa"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder-slate-400"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                วิธีตรวจเช็ก (Method TH) *
              </label>
              <input
                type="text"
                value={itemFormMethodTh}
                onChange={(e) => setItemFormMethodTh(e.target.value)}
                placeholder="เช่น เกจวัด (MPa), สายตา, แมนนวล"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder-slate-400"
                required
              />
            </div>
          </div>

          {/* Conditional Fields: Numeric Gauge */}
          {itemFormInputType === 'numeric_gauge' && (
            <div className="p-4 bg-blue-50/50 border border-blue-200 rounded-xl space-y-3">
              <h4 className="text-xs font-bold text-blue-700 flex items-center gap-1.5">
                <Gauge className="w-4 h-4" /> กำหนดเกณฑ์เกจวัด & ขีดจำกัดความปลอดภัย
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">หน่วยวัด (Unit) *</label>
                  <input
                    type="text"
                    value={itemFormUnit}
                    onChange={(e) => setItemFormUnit(e.target.value)}
                    placeholder="MPa"
                    className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-sm font-mono text-slate-900"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">เกณฑ์ขั้นต่ำ (Min)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemFormMinValue}
                    onChange={(e) => setItemFormMinValue(e.target.value)}
                    placeholder="6.8"
                    className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-sm font-mono text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">เกณฑ์สูงสุด (Max)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemFormMaxValue}
                    onChange={(e) => setItemFormMaxValue(e.target.value)}
                    placeholder="10.0"
                    className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-sm font-mono text-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  ข้อความเตือนเมื่อผิดปกติ (Critical Warning Message)
                </label>
                <input
                  type="text"
                  value={itemFormWarningMsg}
                  onChange={(e) => setItemFormWarningMsg(e.target.value)}
                  placeholder="เช่น แรงดันไฮดรอลิกต่ำกว่าเกณฑ์ความปลอดภัย 6.8 MPa!"
                  className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-xs text-slate-900 placeholder-slate-400"
                />
              </div>
            </div>
          )}

          {/* Conditional Fields: Select Option */}
          {itemFormInputType === 'select_option' && (
            <div className="p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl space-y-3">
              <h4 className="text-xs font-bold text-emerald-700 flex items-center gap-1.5">
                <ListFilter className="w-4 h-4" /> ตัวเลือกสถานะ (อย่างน้อย 2 ตัวเลือก)
              </h4>
              <div className="space-y-2">
                {itemFormOptions.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const next = [...itemFormOptions];
                        next[idx] = e.target.value;
                        setItemFormOptions(next);
                      }}
                      className="flex-1 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm text-slate-900"
                    />
                    {itemFormOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          setItemFormOptions(itemFormOptions.filter((_, i) => i !== idx));
                        }}
                        className="text-rose-600 hover:text-rose-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setItemFormOptions([...itemFormOptions, ''])}
              >
                + เพิ่มตัวเลือก
              </Button>
            </div>
          )}

          {/* Conditional Fields: Operating Hours */}
          {activeFrequency === 'HOURS' && (
            <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-xl space-y-2">
              <h4 className="text-xs font-bold text-amber-700 flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> เกณฑ์รอบชั่วโมงการทำงาน (Hours Threshold)
              </h4>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  step="100"
                  min="1"
                  value={itemFormHoursThreshold}
                  onChange={(e) => setItemFormHoursThreshold(e.target.value)}
                  className="w-48 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm font-mono text-slate-900"
                  required
                />
                <span className="text-xs text-slate-500">ชั่วโมงทำงานสะสม (เช่น 500, 1000, 2000, 8000)</span>
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsItemModalOpen(false)}
            >
              ยกเลิก
            </Button>
            <Button type="submit" variant="primary">
              {editingItem ? 'อัปเดตรายการ' : 'เพิ่มรายการ'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Item Confirmation Modal */}
      <Modal
        isOpen={Boolean(deleteItemConfirmId)}
        onClose={() => setDeleteItemConfirmId(null)}
        title="ยืนยันการลบรายการตรวจเช็ก"
        maxWidth="sm"
      >
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-700">
            คุณแน่ใจหรือไม่ว่าต้องการลบรายการนี้ออกจากเทมเพลต?
          </p>
          <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteItemConfirmId(null)}>
              ยกเลิก
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteItemConfirmId && handleDeleteItem(deleteItemConfirmId)}
            >
              ลบรายการ
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
